import { useEffect } from 'react'
import { showUnsavedChangesModal } from './unsavedChanges'

/**
 * Custom hook to warn users about unsaved changes when navigating away
 * Blocks navigation and shows confirmation modal when hasUnsavedChanges is true
 * Also warns on browser close/refresh
 *
 * @param hasUnsavedChanges - Whether there are unsaved changes
 * @param navigator - React router navigator object
 * @param location - React router location object (optional, for dependency tracking)
 */
export const useUnsavedChangesWarning = (
  hasUnsavedChanges: boolean,
  navigator: any,
  location: any = null
): void => {
  // Block navigation when there are unsaved changes
  useEffect(() => {
    if (!hasUnsavedChanges || !navigator) return

    const push = navigator.push
    const replace = navigator.replace

    navigator.push = (...args: any[]) => {
      showUnsavedChangesModal({
        onConfirm: () => {
          push.apply(navigator, args)
        }
      })
    }

    navigator.replace = (...args: any[]) => {
      showUnsavedChangesModal({
        onConfirm: () => {
          replace.apply(navigator, args)
        }
      })
    }

    return () => {
      navigator.push = push
      navigator.replace = replace
    }
  }, [hasUnsavedChanges, navigator, location])

  // Warn on browser close/refresh when there are unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault()
        e.returnValue = ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [hasUnsavedChanges])
}
